/**
 * Grille de saisie des chiffres tapés à la main : le réalisé du back-office du client
 * et les objectifs qu'il s'est fixés.
 *
 * ⚠️ **Le back-office peut aussi venir d'une boutique connectée**, et les deux se composent : le
 * relevé sert de fond, ce qui est tapé ici l'emporte. La préséance est délibérée — un chiffre
 * saisi à la main l'a souvent été parce que l'automatique était faux — et son pourquoi est écrit
 * dans `serieDesVentesBoutique`, côté serveur. Les objectifs, eux, n'ont aucun équivalent
 * connecté.
 *
 * **La granularité est le choix structurant**, pas un détail de présentation : elle décide de ce
 * qu'une ligne représente, et c'est elle qui est conservée en base. Un objectif posé pour un mois
 * doit se relire pour ce mois — la répartition en jours n'existe que pour le tableau.
 *
 * **La période de la grille est indépendante de celle du dashboard.** Sans quoi on ne pourrait
 * pas saisir décembre en regardant janvier, ce qui est pourtant le geste le plus courant en
 * début d'exercice.
 *
 * **Vide n'est pas zéro.** Un champ laissé vide envoie `null` et la ligne disparaît du tableau ;
 * un « 0 » tapé est un zéro mesuré et s'affiche. C'est la règle de tout le dashboard, et c'est
 * elle qui distingue « pas encore saisi » de « rien vendu ce jour-là ».
 */

import React, { useState, useMemo } from 'react';
import { Loader, AlertCircle } from 'lucide-react';

/**
 * Colonnes par défaut : le back-office et les objectifs.
 *
 * La grille les reçoit en paramètre plutôt que de les connaître, parce qu'elle sert aussi à
 * saisir les coûts — même gestes, même granularités, même règle du vide contre le zéro. Ce qui
 * change d'un usage à l'autre tient dans cette liste.
 */
const CHAMPS_PAR_DEFAUT = [
  { cle: 'boTransactions', titre: 'Transactions BO', entier: true },
  { cle: 'boRevenue', titre: 'CA BO (€)', entier: false },
  { cle: 'objRevenue', titre: 'Objectif CA (€)', entier: false },
  { cle: 'objTransactions', titre: 'Objectif transactions', entier: true }
];

/**
 * Du plus fin au plus large, et le quotidien d'abord.
 *
 * C'est la granularité par défaut des données — celle qu'on saisit au fil de l'eau — et la plus
 * précise : partir du mois obligeait à changer d'échelle pour corriger une journée, le geste le
 * plus courant. Les deux autres restent à portée pour poser un objectif ou rattraper un mois.
 */
const GRANULARITES = [
  { cle: 'JOUR', titre: 'Quotidien' },
  { cle: 'SEMAINE', titre: 'Hebdomadaire' },
  { cle: 'MOIS', titre: 'Mensuel' }
];

const MOIS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

const iso = (d) => d.toISOString().slice(0, 10);

/** Lundi de la semaine ISO contenant cette date. */
const lundiDe = (date) => {
  const d = new Date(date);
  const decalage = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - decalage);
  return d;
};

/**
 * Les périodes à saisir, pour une granularité et une année (ou un mois) donnés.
 *
 * Les bornes sont celles que le serveur exige — un 1er de mois, un lundi —, calculées ici pour
 * que l'écran ne puisse pas proposer une période qu'il refuserait ensuite.
 */
function periodesDe(granularite, annee, mois) {
  if (granularite === 'MOIS') {
    return MOIS_FR.map((nom, i) => ({
      debut: iso(new Date(Date.UTC(annee, i, 1))),
      libelle: `${nom} ${annee}`
    }));
  }

  if (granularite === 'JOUR') {
    const nbJours = new Date(Date.UTC(annee, mois + 1, 0)).getUTCDate();
    return Array.from({ length: nbJours }, (_, i) => {
      const d = new Date(Date.UTC(annee, mois, i + 1));
      return {
        debut: iso(d),
        libelle: d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', timeZone: 'UTC' })
      };
    });
  }

  // Les semaines qui touchent le mois affiché, y compris celles qui débordent : une semaine
  // appartient à deux mois, la saisir depuis l'un ou l'autre doit donner la même ligne.
  const semaines = [];
  const curseur = lundiDe(new Date(Date.UTC(annee, mois, 1)));
  const finDuMois = new Date(Date.UTC(annee, mois + 1, 0));
  while (curseur <= finDuMois) {
    const fin = new Date(curseur);
    fin.setUTCDate(curseur.getUTCDate() + 6);
    semaines.push({
      debut: iso(curseur),
      libelle: `${curseur.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', timeZone: 'UTC' })} → ${fin.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', timeZone: 'UTC' })}`
    });
    curseur.setUTCDate(curseur.getUTCDate() + 7);
  }
  return semaines;
}

/** Une saisie en français devient un nombre : virgule décimale, espaces et « € » tolérés. */
const enNombre = (texte) => {
  const propre = String(texte).replace(/[\s  €]/g, '').replace(',', '.');
  if (propre === '') return null;
  const n = Number(propre);
  return Number.isFinite(n) ? n : NaN;
};

const afficher = (v) => (v === null || v === undefined ? '' : String(v).replace('.', ','));

function GrilleDeSaisie({ saisies, onEnregistrer, enregistrement, erreur, champs = CHAMPS_PAR_DEFAUT }) {
  const CHAMPS = champs;
  const maintenant = new Date();
  const [granularite, setGranularite] = useState('JOUR');
  const [annee, setAnnee] = useState(maintenant.getUTCFullYear());
  const [mois, setMois] = useState(maintenant.getUTCMonth());
  const [brouillon, setBrouillon] = useState({});

  const periodes = useMemo(() => periodesDe(granularite, annee, mois), [granularite, annee, mois]);

  /** Ce qui est en base pour cette granularité, indexé par date de début. */
  const enBase = useMemo(() => {
    const parDebut = {};
    for (const s of saisies) {
      if (s.granularite === granularite) parDebut[s.debut] = s;
    }
    return parDebut;
  }, [saisies, granularite]);

  /**
   * Périodes plus fines déjà saisies à l'intérieur d'une période affichée.
   *
   * C'est l'information qui manque le plus à qui saisit un mois : une journée posée à part
   * remplace sa quote-part, et le total du mois cesse d'être celui qu'on vient de taper. Le dire
   * ici évite de le découvrir dans le tableau, où plus rien ne l'expliquerait.
   */
  const plusFinesDans = (debut) => {
    if (granularite === 'JOUR') return 0;
    const d = new Date(`${debut}T00:00:00Z`);
    const fin = new Date(d);
    if (granularite === 'MOIS') fin.setUTCMonth(d.getUTCMonth() + 1, 0);
    else fin.setUTCDate(d.getUTCDate() + 6);

    const plusFines = granularite === 'MOIS' ? ['SEMAINE', 'JOUR'] : ['JOUR'];
    return saisies.filter(s => plusFines.includes(s.granularite)
      && s.debut <= iso(fin) && s.fin >= debut).length;
  };

  /** Dernier jour de la période affichée sur une ligne, selon la granularité en cours. */
  const finDeLigne = (debut) => {
    const d = new Date(`${debut}T00:00:00Z`);
    if (granularite === 'JOUR') return d;
    const fin = new Date(d);
    if (granularite === 'MOIS') fin.setUTCMonth(d.getUTCMonth() + 1, 0);
    else fin.setUTCDate(d.getUTCDate() + 6);
    return fin;
  };

  const PLUS_LARGES = { JOUR: ['SEMAINE', 'MOIS'], SEMAINE: ['MOIS'], MOIS: [] };

  /**
   * Ce qu'une période plus large répand sur cette ligne.
   *
   * Un mois saisi n'écrit aucune ligne quotidienne : la grille du jour paraissait donc vide
   * alors que le tableau, lui, comptait bien le montant réparti. L'indication le dit.
   *
   * Elle reste une **indication grisée**, jamais une valeur pré-remplie : l'enregistrer
   * écrirait trente lignes quotidiennes, qui primeraient ensuite sur le mois — le total
   * cesserait de suivre la saisie mensuelle sans que rien ne le signale.
   */
  const heriteDe = (debut, cle) => {
    const larges = PLUS_LARGES[granularite] || [];
    if (larges.length === 0) return null;

    const finLigne = iso(finDeLigne(debut));
    const enJours = (d1, d2) => Math.round(
      (new Date(`${d2}T00:00:00Z`) - new Date(`${d1}T00:00:00Z`)) / 86400000
    ) + 1;

    let total = null;
    for (const s of saisies) {
      if (!larges.includes(s.granularite)) continue;
      if (s[cle] == null) continue;
      if (s.debut > finLigne || s.fin < debut) continue;

      const joursSaisie = enJours(s.debut, s.fin);
      if (joursSaisie <= 0) continue;
      const joursCouverts = enJours(
        s.debut > debut ? s.debut : debut,
        s.fin < finLigne ? s.fin : finLigne
      );
      total = (total || 0) + (s[cle] / joursSaisie) * joursCouverts;
    }
    return total;
  };

  const valeurDe = (debut, cle) => {
    const cleBrouillon = `${granularite}|${debut}|${cle}`;
    if (cleBrouillon in brouillon) return brouillon[cleBrouillon];
    return afficher(enBase[debut]?.[cle]);
  };

  const changer = (debut, cle, texte) => {
    setBrouillon(b => ({ ...b, [`${granularite}|${debut}|${cle}`]: texte }));
  };

  /** Les périodes touchées depuis l'ouverture : rien d'autre n'est renvoyé au serveur. */
  const modifiees = useMemo(() => {
    const debuts = new Set();
    for (const cle of Object.keys(brouillon)) {
      const [g, debut] = cle.split('|');
      if (g === granularite) debuts.add(debut);
    }
    return [...debuts];
  }, [brouillon, granularite]);

  const fautesDeSaisie = useMemo(() => {
    const fautes = [];
    for (const debut of modifiees) {
      for (const { cle, titre, entier } of CHAMPS) {
        const brut = valeurDe(debut, cle);
        if (brut === '') continue;
        const n = enNombre(brut);
        if (Number.isNaN(n)) fautes.push(`${debut} — ${titre} : « ${brut} » n'est pas un nombre`);
        else if (n < 0) fautes.push(`${debut} — ${titre} : valeur négative`);
        else if (entier && !Number.isInteger(n)) fautes.push(`${debut} — ${titre} : nombre entier attendu`);
      }
    }
    return fautes;
  }, [modifiees, brouillon, enBase]); // eslint-disable-line react-hooks/exhaustive-deps

  const enregistrer = () => {
    const aEnvoyer = modifiees.map(debut => {
      const ligne = { granularite, debut };
      for (const { cle } of CHAMPS) {
        const brut = valeurDe(debut, cle);
        ligne[cle] = brut === '' ? null : enNombre(brut);
      }
      return ligne;
    });
    onEnregistrer(aEnvoyer, () => setBrouillon({}));
  };

  const boutonPeriode = (actif, contenu, onClick) => (
    <button
      onClick={onClick}
      style={{
        padding: '6px 12px', borderRadius: '6px', fontSize: '0.8125rem', cursor: 'pointer',
        border: '1px solid ' + (actif ? '#7c2d12' : '#e5e7eb'),
        background: actif ? '#7c2d12' : '#fff', color: actif ? '#fff' : '#374151'
      }}
    >
      {contenu}
    </button>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' }}>
        {GRANULARITES.map(g => boutonPeriode(g.cle === granularite, g.titre, () => setGranularite(g.cle)))}

        <span style={{ width: '12px' }} />

        {boutonPeriode(false, '◀', () => (granularite === 'MOIS' ? setAnnee(annee - 1) : (mois === 0 ? (setAnnee(annee - 1), setMois(11)) : setMois(mois - 1))))}
        <span style={{ fontSize: '0.875rem', fontWeight: 600, minWidth: '130px', textAlign: 'center' }}>
          {granularite === 'MOIS' ? annee : `${MOIS_FR[mois]} ${annee}`}
        </span>
        {boutonPeriode(false, '▶', () => (granularite === 'MOIS' ? setAnnee(annee + 1) : (mois === 11 ? (setAnnee(annee + 1), setMois(0)) : setMois(mois + 1))))}
      </div>

      <div style={{ maxHeight: '48vh', overflowY: 'auto', border: '1px solid #f1f5f9', borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
          <thead>
            <tr style={{ position: 'sticky', top: 0, background: '#faf7f5', zIndex: 1 }}>
              <th style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600, color: '#7c2d12' }}>Période</th>
              {CHAMPS.map(c => (
                <th key={c.cle} style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 600, color: '#7c2d12' }}>{c.titre}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {periodes.map(p => {
              const finesDedans = plusFinesDans(p.debut);
              return (
                <tr key={p.debut} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                    {p.libelle}
                    {finesDedans > 0 && (
                      <span
                        title={`${finesDedans} période(s) plus fine(s) sont saisies à l'intérieur : elles remplacent leur part de cette répartition, le total effectif diffère donc de la valeur saisie ici.`}
                        style={{ marginLeft: '6px', color: '#b45309', cursor: 'help' }}
                      >
                        ⚠ {finesDedans}
                      </span>
                    )}
                  </td>
                  {CHAMPS.map(c => {
                    const herite = heriteDe(p.debut, c.cle);
                    return (
                      <td key={c.cle} style={{ padding: '4px 6px', textAlign: 'right' }}>
                        <input
                          value={valeurDe(p.debut, c.cle)}
                          onChange={e => changer(p.debut, c.cle, e.target.value)}
                          // L'indication n'apparaît que sur un champ vide : dès qu'une valeur
                          // propre est saisie, c'est elle qui vaut, et l'héritage ne s'applique
                          // plus à cette période.
                          placeholder={herite == null ? '—' : afficher(Math.round(herite * 100) / 100)}
                          title={herite == null ? undefined
                            : `Réparti depuis une saisie ${granularite === 'JOUR' ? 'hebdomadaire ou mensuelle' : 'mensuelle'}. `
                              + 'Le tableau en tient déjà compte. Saisir une valeur ici la remplacera pour cette période.'}
                          style={{
                            width: '100%', maxWidth: '120px', padding: '5px 8px', textAlign: 'right',
                            border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.8125rem'
                          }}
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {fautesDeSaisie.length > 0 && (
        <div style={{ marginTop: '10px', padding: '8px 10px', background: '#fef2f2', borderRadius: '6px', fontSize: '0.8125rem', color: '#b91c1c' }}>
          {fautesDeSaisie.slice(0, 4).map((f, i) => <div key={i}>{f}</div>)}
          {fautesDeSaisie.length > 4 && <div>… et {fautesDeSaisie.length - 4} autre(s)</div>}
        </div>
      )}

      {erreur && (
        <div style={{ marginTop: '10px', padding: '8px 10px', background: '#fef2f2', borderRadius: '6px', fontSize: '0.8125rem', color: '#b91c1c', display: 'flex', gap: '6px' }}>
          <AlertCircle size={15} style={{ flexShrink: 0 }} /> <span>{erreur}</span>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '14px' }}>
        <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
          {/* Le décompte suit les colonnes affichées : « les quatre vides » était juste
              tant que la grille en montrait quatre, faux depuis qu'elle est paramétrable. */}
          {modifiees.length === 0
            ? `Un champ vidé efface la valeur${CHAMPS.length > 1 ? ` ; les ${CHAMPS.length === 2 ? 'deux' : CHAMPS.length} vides retirent la période` : ' et retire la période'}.`
            : `${modifiees.length} période(s) modifiée(s)`}
        </span>
        <button
          onClick={enregistrer}
          disabled={modifiees.length === 0 || fautesDeSaisie.length > 0 || enregistrement}
          style={{
            padding: '9px 18px', borderRadius: '8px', border: 'none', fontSize: '0.875rem', fontWeight: 500,
            background: modifiees.length === 0 || fautesDeSaisie.length > 0 ? '#e5e7eb' : '#7c2d12',
            color: modifiees.length === 0 || fautesDeSaisie.length > 0 ? '#9ca3af' : '#fff',
            cursor: modifiees.length === 0 || fautesDeSaisie.length > 0 ? 'default' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: '7px'
          }}
        >
          {enregistrement && <Loader size={14} className="spin" />}
          Enregistrer
        </button>
      </div>
    </div>
  );
}

export default GrilleDeSaisie;
export { enNombre, periodesDe };
